/**
 * smAnalytics.ts — reads Sociamonials' three report endpoints (workspace analytics, published messages, best times).
 *
 * Their documentation describes what each report contains but not the exact reply layout, so every reader here accepts the
 * layouts that are plausible, takes only what it can read, and reports anything else as "not available" instead of
 * guessing. A number that is missing is null, never 0.
 */
import { z } from "zod";

export const SM_RANGES = ["last_30_days", "last_60_days", "last_90_days"] as const;
export type SmRange = (typeof SM_RANGES)[number];
export const SM_RANGE_LABEL: Record<SmRange, string> = { last_30_days: "Last 30 days", last_60_days: "Last 60 days", last_90_days: "Last 90 days" };
export const smRangeDays = (r: SmRange): number => (r === "last_30_days" ? 30 : r === "last_60_days" ? 60 : 90);

const obj = (v: unknown): Record<string, unknown> | null => (v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : null);
const num = (v: unknown): number | null => {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim() !== "" && Number.isFinite(Number(v))) return Number(v);
  return null;
};
const str = (v: unknown): string | null => (typeof v === "string" && v.trim() !== "" ? v.trim() : null);
/** The report body, whether or not it is wrapped in `data`. */
const body = (raw: unknown): Record<string, unknown> => {
  const o = obj(raw) ?? {};
  return obj(o.data) ?? o;
};

const NETWORK_LABEL: Record<string, string> = {
  facebook: "Facebook",
  linkedin: "LinkedIn",
  twitter: "X",
  x: "X",
  youtube: "YouTube",
  instagram: "Instagram",
  bluesky: "Bluesky",
  gbp: "Google Business",
  pinterest: "Pinterest",
  tiktok: "TikTok",
  threads: "Threads",
};
const networkLabel = (n: string) => NETWORK_LABEL[n.toLowerCase()] ?? n;

// ------------------------------------------------------------------------------------------------ delivery

export const smDeliverySchema = z.object({
  totals: z.object({ created: z.number().nullable(), delivered: z.number().nullable(), pending: z.number().nullable(), drafts: z.number().nullable(), canceled: z.number().nullable() }),
  byNetwork: z.array(z.object({ network: z.string(), label: z.string(), targeted: z.number().nullable(), delivered: z.number().nullable() })),
  /** Sociamonials says this report is delivery-only. */
  deliveryOnly: z.boolean(),
});
export type SmDelivery = z.infer<typeof smDeliverySchema>;

export function parseSmDelivery(raw: unknown): SmDelivery {
  const b = body(raw);
  const t = obj(b.totals) ?? {};
  const meta = obj(obj(raw)?.meta) ?? obj(b.meta) ?? {};
  const rows: SmDelivery["byNetwork"] = [];
  const bn = b.by_network;
  if (Array.isArray(bn)) {
    for (const r of bn) {
      const o = obj(r);
      const n = o ? str(o.network) ?? str(o.name) : null;
      if (o && n) rows.push({ network: n, label: networkLabel(n), targeted: num(o.targeted), delivered: num(o.delivered) });
    }
  } else if (obj(bn)) {
    for (const [n, v] of Object.entries(bn as Record<string, unknown>)) {
      const o = obj(v);
      if (o) rows.push({ network: n, label: networkLabel(n), targeted: num(o.targeted), delivered: num(o.delivered) });
    }
  }
  return {
    totals: { created: num(t.created), delivered: num(t.delivered), pending: num(t.pending), drafts: num(t.drafts), canceled: num(t.canceled) },
    byNetwork: rows,
    deliveryOnly: meta.partial_data !== undefined && meta.partial_data !== null && meta.partial_data !== false,
  };
}

// ------------------------------------------------------------------------------------------------ published messages

export const smPublishedMessageSchema = z.object({
  id: z.string().nullable(),
  publishedAt: z.string().nullable(),
  publisher: z.string().nullable(),
  message: z.string(),
  networks: z.array(z.string()),
  links: z.number().nullable(),
  clicks: z.number().nullable(),
  leads: z.number().nullable(),
  sales: z.number().nullable(),
  revenue: z.number().nullable(),
  likes: z.number().nullable(),
  comments: z.number().nullable(),
});
export type SmPublishedMessage = z.infer<typeof smPublishedMessageSchema>;

const NETWORK_KEYS = Object.keys(NETWORK_LABEL).filter((k) => k !== "x");

function networksOf(o: Record<string, unknown>): string[] {
  const out: string[] = [];
  const nested = obj(o.networks) ?? obj(o.targets);
  const list = Array.isArray(o.networks) ? o.networks : null;
  if (list) for (const n of list) if (typeof n === "string") out.push(networkLabel(n));
  const flags = nested ?? o;
  for (const k of NETWORK_KEYS) if (flags[k] === true || flags[k] === 1 || flags[k] === "1") out.push(networkLabel(k));
  return [...new Set(out)];
}

export function parseSmPublishedMessages(raw: unknown): { items: SmPublishedMessage[]; total: number | null } {
  const b = body(raw);
  const list = Array.isArray(b.messages) ? b.messages : Array.isArray(raw) ? (raw as unknown[]) : Array.isArray(b.posts) ? b.posts : [];
  const items = list.flatMap((r): SmPublishedMessage[] => {
    const o = obj(r);
    if (!o) return [];
    const message = str(o.message) ?? str(o.text) ?? "";
    const id = o.post_id ?? o.id;
    return [
      {
        id: id === undefined || id === null ? null : String(id),
        publishedAt: str(o.published_at_utc) ?? str(o.published_at),
        publisher: str(o.publisher),
        message: message.slice(0, 500),
        networks: networksOf(o),
        links: num(o.links),
        clicks: num(o.clicks),
        leads: num(o.leads),
        sales: num(o.sales),
        revenue: num(o.revenue),
        likes: num(o.likes),
        comments: num(o.comments),
      },
    ];
  });
  const m = obj(obj(raw)?.meta) ?? obj(b.meta) ?? {};
  return { items, total: num(m.total) ?? num(b.total) ?? null };
}

// ------------------------------------------------------------------------------------------------ best times

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
export const smBestTimesSchema = z.object({
  /** Sociamonials says it has enough posts to recommend a time. */
  available: z.boolean(),
  confidence: z.string().nullable(),
  /** Day and hour (0 to 23, in the workspace's clock) with the lift against the workspace's own average, when given. */
  slots: z.array(z.object({ day: z.string(), hour: z.number().int().min(0).max(23), lift: z.number().nullable(), posts: z.number().nullable() })),
  /** The report said there is a recommendation but not in a form that could be read here. */
  unreadable: z.boolean(),
});
export type SmBestTimes = z.infer<typeof smBestTimesSchema>;

function dayName(v: unknown): string | null {
  if (typeof v === "number" && Number.isInteger(v)) return DAYS[((v % 7) + 7) % 7] ?? null; // 0 = Sunday
  const s = str(v);
  if (!s) return null;
  const hit = DAYS.find((d) => d.toLowerCase().startsWith(s.toLowerCase().slice(0, 3)));
  return hit ?? null;
}

export function parseSmBestTimes(raw: unknown): SmBestTimes {
  const b = body(raw);
  const rec = obj(b.recommendation);
  if (!rec) return { available: false, confidence: null, slots: [], unreadable: false };
  const confidence = str(rec.confidence);
  // Sociamonials: available=false or Low confidence is no signal.
  const available = rec.available === true && (confidence ?? "").toLowerCase() !== "low";
  const candidates = [rec.slots, rec.top_slots, rec.best_slots, rec.recommended, rec.top, rec.items].find((x) => Array.isArray(x)) as unknown[] | undefined;
  const slots = (candidates ?? []).flatMap((r) => {
    const o = obj(r);
    if (!o) return [];
    const day = dayName(o.day_of_week ?? o.day ?? o.dow);
    const hour = num(o.hour ?? o.hour_of_day ?? o.start_hour);
    if (!day || hour === null || !Number.isInteger(hour) || hour < 0 || hour > 23) return [];
    return [{ day, hour, lift: num(o.lift ?? o.lift_pct ?? o.lift_percent), posts: num(o.posts ?? o.post_count ?? o.n) }];
  });
  return { available, confidence, slots: available ? slots : [], unreadable: available && slots.length === 0 };
}

// ------------------------------------------------------------------------------------------------ one screen

const section = <T extends z.ZodTypeAny>(data: T) => z.discriminatedUnion("ok", [z.object({ ok: z.literal(true), data }), z.object({ ok: z.literal(false), error: z.string(), code: z.string().nullable() })]);

export const smAnalyticsResponseSchema = z.object({
  range: z.enum(SM_RANGES),
  workspaceName: z.string().nullable(),
  delivery: section(smDeliverySchema),
  posts: section(z.object({ items: z.array(smPublishedMessageSchema), total: z.number().nullable() })),
  bestTimes: section(smBestTimesSchema),
  fetchedAt: z.string(),
});
export type SmAnalyticsResponse = z.infer<typeof smAnalyticsResponseSchema>;
export const smAnalyticsQuerySchema = z.object({ siteId: z.string().min(1).max(100), range: z.enum(SM_RANGES).default("last_30_days") });

/** The most posts shown from the published-messages report. */
export const SM_POSTS_SHOWN = 25;
